import React, { useRef, useEffect, useMemo } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { LampGeometry } from 'lamp-core';

// Base lamp configuration for previews
const PREVIEW_CONFIG = {
  diameter: 100,
  height: 200,
  tubularSegments: 32,
  radialSegments: 16,
  profilePoints: [
    { x: 0, y: 0 },
    { x: 50, y: 0 },
    { x: 50, y: 200 },
    { x: 0, y: 200 }
  ]
};

// Effect-specific modifier configurations
const EFFECT_MODIFIERS = {
  displacement: {
    type: 'displacement',
    parameters: {
      strength: 10,
      scale: 0.5
    }
  },
  twist: {
    type: 'twist',
    parameters: {
      angle: 180
    }
  },
  radial_wave: {
    type: 'radial_wave',
    parameters: {
      strength: 15,
      frequency: 8,
      waveType: 'sine'
    }
  },
  radial_wrinkle: {
    type: 'radial_wrinkle',
    parameters: {
      perimeter: 8,
      variation: 0.3
    }
  },
  heightmap_noise: {
    type: 'heightmap_noise',
    parameters: {
      amplitude: 10,
      frequency: 0.5,
      layers: 3
    }
  }
};

function PreviewLamp({ effectType, isPlaying }) {
  const meshRef = useRef();
  const modifier = EFFECT_MODIFIERS[effectType];
  
  // Create geometry with the specific effect
  const geometry = useMemo(() => {
    const lampGeometry = new LampGeometry(
      PREVIEW_CONFIG,
      [modifier],
      123 // Fixed seed for consistent preview
    );
    return lampGeometry.getGeometry();
  }, [effectType]);

  // Create material
  const material = useMemo(() => {
    return new THREE.MeshStandardMaterial({
      color: '#ffffff',
      roughness: 0.5,
      metalness: 0.1,
      side: THREE.DoubleSide
    });
  }, []);

  // Animation loop
  useFrame((state) => {
    if (meshRef.current && isPlaying) {
      // Rotate the lamp slowly to show the effect
      meshRef.current.rotation.y = state.clock.elapsedTime * 0.5;
    }
  });

  return (
    <mesh ref={meshRef} geometry={geometry} material={material} />
  );
}

function EffectPreview({ effectType, isPlaying = false, width = 280, height = 200 }) {
  return (
    <div style={{ width, height, background: '#f5f5f5', borderRadius: '6px', overflow: 'hidden' }}>
      <Canvas
        camera={{ position: [0, 100, 300], fov: 30 }}
        style={{ width: '100%', height: '100%' }}
      >
        <ambientLight intensity={0.6} />
        <directionalLight position={[100, 100, 100]} intensity={0.8} />
        <PreviewLamp effectType={effectType} isPlaying={isPlaying} />
      </Canvas>
    </div>
  );
}

export default EffectPreview; 